import { type FC, useRef, useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { EnvelopeIcon, UserIcon } from "@heroicons/react/24/solid";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { Spinner } from "@via/components/loading/loading";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import { importPipelineOptions } from "@via/utils/form-validators.tsx";
import { SemanticRevisionTypes, USER_ROLES } from "@via/utils/constants.tsx";
import { useIntegrationTokens } from "@via/api/integration-tokens.api.tsx";
import { getTokenTypeIcon } from "@via/pages/account/profile/integration-tokens/utils/helper";
import { useImportPipeline } from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { ImportPipelineRequestBody, PipelineType } from "common";
import { extractSha } from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import {
  calculateVersion,
  checkIsSemanticVersion,
} from "@via/utils/helpers.tsx";
import { useAuth } from "@via/hooks/useAuth";

interface ImportPipelineFormProps {
  setOpen: (open: boolean) => void;
  pipelineDraft?: any;
  showImportViaPipelineButton?: boolean;
  openImportViaPipelineModal?: (open: boolean) => void;
}

const REPOSITORY_TYPE_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Private", value: "private" },
];

const PIPELINE_TYPE_OPTIONS = [
  { label: PipelineType.nfCore, value: PipelineType.nfCore },
  { label: PipelineType.nextFlow, value: PipelineType.nextFlow },
];

const ImportPipelineForm: FC<ImportPipelineFormProps> = ({
  setOpen,
  pipelineDraft,
  showImportViaPipelineButton = false,
  openImportViaPipelineModal,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOverwritePipeline, setIsOverwritePipeline] = useState(true);
  const { resetChangeCount } = useChangesDetect();
  const [revisionType, setRevisionType] = useState(SemanticRevisionTypes[0]);
  const [displayVersion, setDisplayVersion] = useState("");
  const { setNotification } = useNotifications();

  const {
    mutate: importPipeline,
    data: newPipelineData,
    isSuccess: isImportPipelineSuccess,
    isPending: isImportPipelineLoading,
  } = useImportPipeline();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ImportPipelineRequestBody>({
    mode: "onChange",
    defaultValues: {
      repositoryType: "public",
      repositoryUrl: "",
      branchOrTag: "",
      pipelineType: PipelineType.nfCore,
      initSubmodules: false,
    },
  });

  useEffect(() => {
    if (newPipelineData) {
      navigate(`/pipelines/${newPipelineData.id}`);
      setOpen(false);
    }
  }, [isImportPipelineSuccess]);

  const [tokensPage, setTokensPage] = useState(0);
  const tokenOptionSize = 10;

  const { data: gitCredentials, isLoading: isGitCredentialsLoading } =
    useIntegrationTokens(
      tokensPage,
      tokenOptionSize,
      [{ id: "createdAt", desc: true }],
      [{ id: "tokenTypeIn", value: ["github", "bitbucket", "gitlab"] }]
    );

  const fetchMoreGitCredentials = useCallback(() => {
    setTokensPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage =
        Math.ceil((gitCredentials?.total ?? 0) / tokenOptionSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [gitCredentials?.total]);

  const selectedRepositoryType = watch("repositoryType");

  const onSubmit = (data: ImportPipelineRequestBody) => {
    const { revComment, ...rest } = data;
    const {
      dateCreated,
      dateModified,
      isEditingAllowed,
      isAdmin,
      isOwner,
      pipelineUUId,
      pipelineRevUUId,
      extractedPipeScript: _extractedPipeScript,
      ...pipelineData
    } = pipelineDraft ?? {};

    importPipeline({
      ...rest,
      ...(!isOverwritePipeline && {
        revisionType: revisionType.value,
        revComment,
      }),
      ...(pipelineDraft && { pipeline: pipelineData }),
    });
    resetChangeCount();
  };

  const formatTokenType = (str: string): string =>
    str ? `${str[0].toUpperCase()}${str.slice(1).toLowerCase()}` : "";

  useEffect(() => {
    if (!pipelineDraft || isOverwritePipeline) return;

    const newVersion = calculateVersion(
      revisionType.value,
      pipelineDraft?.version ?? ""
    );
    setDisplayVersion(newVersion);
  }, [pipelineDraft, isOverwritePipeline]);

  const handleRevisionTypeChange = selected => {
    const isValidVersion = checkIsSemanticVersion(
      pipelineDraft?.version ?? "",
      true
    );
    if (!isValidVersion) {
      setNotification({
        message: "Invalid Version Detected",
        details: "Acceptable Versions Must Be Semantic (x.x.x)",
        variant: NOTIFICATION.ERROR,
      });
      return;
    }
    const newVersion = calculateVersion(
      selected.value,
      pipelineDraft?.version ?? ""
    );
    setDisplayVersion(newVersion);
    setRevisionType(selected);
  };

  return (
    <>
      <form
        ref={formRef}
        onSubmit={e => {
          e.preventDefault();
          handleSubmit(onSubmit)(e);
        }}
        className="py-4">
        {user?.userRole === USER_ROLES.ADMIN && showImportViaPipelineButton && (
          <button
            type="button"
            className="mb-4 w-full inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={() => {
              setOpen(false);
              openImportViaPipelineModal?.(true);
            }}
            title="Import Via Pipeline">
            Import Via Pipeline
          </button>
        )}
        {/* Repository Type */}
        <label className="mb-2 block text-sm font-medium text-gray-900">
          Repository Type <span className="text-red-400">*</span>
        </label>
        <SelectComponent
          control={control}
          name="repositoryType"
          rules={importPipelineOptions.repositoryType}
          options={REPOSITORY_TYPE_OPTIONS}
          placeholder="Select Repository Type"
        />
        {errors.repositoryType && (
          <span className="text-xs text-rose-500">
            {errors.repositoryType.message}
          </span>
        )}

        {/* Account (Only for Private) */}
        {selectedRepositoryType === "private" && (
          <>
            <label className="mt-4 mb-2 block text-sm font-medium text-gray-900">
              Git Account <span className="text-red-400">*</span>
            </label>
            <SelectComponent
              control={control}
              name="serviceTokenId"
              rules={importPipelineOptions.serviceTokenId}
              options={
                gitCredentials?.data?.map(
                  ({ name, id, credentialData, tokenType }) => ({
                    label: name,
                    value: id,
                    icon: getTokenTypeIcon(tokenType),
                    metadata: { ...credentialData, tokenType },
                  })
                ) ?? []
              }
              placeholder="Select Git Account"
              isLoading={isGitCredentialsLoading}
              onFetchMoreOptions={fetchMoreGitCredentials}
              formatOptionLabel={(option, { context }) =>
                context === "menu" ? (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      {/* Git Account Name */}
                      <img
                        className="w-5"
                        alt={option.label}
                        src={option.icon}
                      />
                      <div>
                        <span className="font-medium">
                          {`${formatTokenType(option.metadata.tokenType)}: `}
                        </span>
                        {option.label}
                      </div>
                    </div>

                    {option.metadata && (
                      <div className="text-sm text-gray-600">
                        {/* Username */}
                        <div className="flex items-center gap-1">
                          <UserIcon className="h-4 w-4 text-indigo-600" />
                          <span className="font-medium">Username:</span>
                          <span>{option.metadata.username}</span>
                        </div>

                        {/* Last Updated Date */}
                        <div className="flex items-center gap-1">
                          <EnvelopeIcon className="h-4 w-4 text-red-600" />
                          <span className="font-medium">Email:</span>
                          <span>{option.metadata.email}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <img className="w-5" alt={option.label} src={option.icon} />
                    <div>
                      <span className="font-medium">
                        {`${formatTokenType(option.metadata.tokenType)}: `}
                      </span>
                      {option.label}
                    </div>
                  </div>
                )
              }
            />
            {errors.serviceTokenId && (
              <span className="text-xs text-rose-500">
                {errors.serviceTokenId.message}
              </span>
            )}
          </>
        )}

        {/* Repository URL */}
        <FormInputComponent
          control={control}
          name="repositoryUrl"
          label="Repository URL"
          placeholder="e.g. https://github.com/nf-core/rnaseq"
          validations={importPipelineOptions.repositoryUrl}
          showRequiredField
        />

        {/* Branch or Tag */}
        <FormInputComponent
          control={control}
          name="branchOrTag"
          label="Branch or Tag Name (optional)"
          placeholder="e.g. main or v1.2.3"
        />

        {/* Pipeline Type */}
        <label className="mt-4 mb-2 block text-sm font-medium text-gray-900">
          Pipeline Type <span className="text-red-400">*</span>
        </label>
        <SelectComponent
          control={control}
          name="pipelineType"
          rules={importPipelineOptions.pipelineType}
          options={PIPELINE_TYPE_OPTIONS}
          placeholder="Select Pipeline Type"
        />
        {errors.pipelineType && (
          <span className="text-xs text-rose-500">
            {errors.pipelineType.message}
          </span>
        )}

        {/* Submodules */}
        <div className="mt-4">
          <label className="flex items-start space-x-2 text-gray-900 sm:text-sm sm:leading-6">
            <FormInputComponent
              control={control}
              name="initSubmodules"
              type="checkbox"
              className="w-4 h-4 mt-1"
            />
            <span className="flex flex-col">
              <span className="font-medium">Include submodules</span>
              <span className="text-sm text-gray-600">
                Also fetch and check out any git submodules during import.
              </span>
            </span>
          </label>
        </div>

        {/* Overwrite or New Revision */}
        {pipelineDraft && (
          <>
            <div className="flex gap-4 mt-4 mb-2">
              <label className="flex items-center space-x-2 text-gray-900 sm:text-sm sm:leading-6">
                <input
                  type="radio"
                  checked={isOverwritePipeline}
                  onChange={() => setIsOverwritePipeline(true)}
                  className="text-indigo-600 focus:ring-indigo-600"
                />
                <span>Overwrite</span>
              </label>

              <label className="flex items-center space-x-2 text-gray-900 sm:text-sm sm:leading-6">
                <input
                  type="radio"
                  checked={!isOverwritePipeline}
                  onChange={() => setIsOverwritePipeline(false)}
                  className="text-indigo-600 focus:ring-indigo-600"
                />
                <span>New Revision</span>
              </label>
            </div>
            {!isOverwritePipeline && (
              <>
                <span className="text-sm text-gray-600">
                  The current version of this pipeline is{" "}
                  <span className="font-medium">{pipelineDraft?.version}</span>{" "}
                  and the new version will be{" "}
                  <span className="font-medium">{displayVersion}</span>
                </span>
                <label className="mt-4 mb-2 block text-sm font-medium text-gray-900">
                  Revision Type
                </label>
                <SelectComponent
                  enableClear={false}
                  name="revisionTypes"
                  onChange={handleRevisionTypeChange}
                  options={SemanticRevisionTypes}
                  value={revisionType}
                />
                <label className="mt-4 mb-2 block text-sm font-medium text-gray-900">
                  SHA:{" "}
                  <span className="font-normal">
                    {extractSha(pipelineDraft?.revComment)}
                  </span>
                </label>
                <FormTextAreaComponent
                  control={control}
                  name="revComment"
                  label="Comment"
                  placeholder="Enter a Revision Comment"
                />
              </>
            )}
          </>
        )}
      </form>
      <div className="modal-footer flex justify-end space-x-2">
        <button
          id="import-pipeline-modal-confirm-button"
          data-testid="modalConfirmButton"
          type="submit"
          onClick={() => formRef.current?.requestSubmit()}
          disabled={isImportPipelineLoading}
          className="via-modal inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600">
          <Spinner loading={isImportPipelineLoading}>Pull Pipeline</Spinner>
        </button>
        <button
          id="import-pipeline-modal-cancel-button"
          data-testid="modalCancelButton"
          type="button"
          onClick={() => setOpen(false)}
          className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-gray-600 hover:bg-gray-500 focus-visible:outline-gray-600">
          Cancel
        </button>
      </div>
    </>
  );
};

export default ImportPipelineForm;
